<template>
  <div>
    <div
      @click="handleToggle"
      @keydown.enter="handleToggle"
      class="px-8 py-6 w-full text-left text-white text-sm md:text-xs uppercase font-medium outline-none focus:outline-none
        active:outline-none focus:bg-secondary-600 dark:focus:bg-white/10 transition-all flex flex-nowrap justify-between items-center cursor-pointer subSideMenuButton whitespace-wrap select-none
        lg:pl-3 lg:pr-2 lg:py-2 lg:text-gray-600 dark:lg:text-white/60 lg:font-medium lg:hover:bg-gray-lightest/80 dark:lg:hover:bg-white/[.04] lg:focus:bg-gray-lightest/80 dark:lg:focus:bg-white/[.04] lg:border-l-4 lg:hover:border-primary dark:lg:hover:border-primary-300/70 lg:focus:border-primary dark:lg:focus:border-primary-300/70
      "
      :class="isDeep ? 'lg:border-gray-200 dark:lg:border-navy-500/60' : 'lg:border-white dark:lg:border-navy-900'"
      tabindex="0"
    >
      {{ option.label }}
      <button class="p-2" tabindex="-1">
        <div
          class="flex items-center"
          :class="
            expanded
              ? 'transition-all transform rotate-180'
              : 'transition-all transform rotate-0'
          "
        >
          <font-awesome-icon icon="fa-solid fa-chevron-down" size="lg" class="h-4 w-4" />
        </div>
      </button>
    </div>
    <ul
      class="w-full lg:bg-transparent transform transition-all duration-150 overflow-hidden"
      :class="expansionClasses"
    >
      <li v-for="navItem in option.subnav" @click="select" :key="navItem.label" :ref="`nav-${navItem.label}`">
        <router-link
          v-if="checkOptionsSidemenuPermissions(navItem)"
          :tabindex="expanded ? 0 : -1"
          :to="urlLink(navItem.route)"
          @click="select"
          class="uppercase font-medium text-sm md:text-xs focus:outline-none focus:bg-secondary-600 focus:bg-white/5"
          :class="navItemClass(navItem.route)"
        >
          {{ navItem.label }}
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex"

export default {
  name: "OptionWithSubnav",
  props: {
    option: {
      type: Object,
      required: true,
    },
    subsection: {
      type: String,
      required: true,
    },
    isDeep: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: false,
      clickedOpen: false,
      hideSubnav: false
    }
  },
  computed: {
    ...mapState(["sideMenuVisible", "screen", "orientation"]),
    ...mapGetters([
      "getIsTenantSuperuser",
      "getUserManagementAccess",
      "getPermissionSet",
      "getAvailableViews",
      "getAvailableFeatures",
      "getPOSSystem",
      "getSiteCommandControllerFeatures",
      "getConfigVersion"
    ]),
    path() {
      return this.$route.fullPath;
    },
    availableViews() {
      return this.getAvailableViews?.[this.$route.meta.module][this.option.feature]
    },
    availableFeatures() {
      return this.getAvailableFeatures?.[this.$route.meta.module]
    },
    isC5() {
      if (this.subsection === "SiteCommand") return this.getConfigVersion === "C5000"
      return false
    },
    supportsInterpolateSize() {
      return CSS.supports('interpolate-size', 'allow-keywords')
    },
    expansionClasses() {
      let classes
      // PREFERRED
      if (this.supportsInterpolateSize) {
        classes = "fadein transition-all duration-150 rounded-b [interpolate-size:allow-keywords] overflow-hidden "
        if (this.expanded) classes += "visible h-auto opacity-100 lg:pt-0 lg:pb-5 "
        else classes += "invisible h-0 opacity-0 lg:py-0 "
      }
      // FALLBACK
      else {
        classes = "fadein transition-all duration-100 rounded-b overflow-hidden "
        if (this.expanded) classes += "visible max-h-[1000px] opacity-100 lg:pt-0 lg:pb-5 "
        else classes += "invisible max-h-0 opacity-50 lg:py-0 "
      }
      return classes
    },
  },
  watch: {
    sideMenuVisible() {
      this.hideSubnav = true
      for (let i = 0; i < this.option.subnav.length; i++) {
        if (this.$route.path.includes(this.option.subnav[i].route)) {
          this.hideSubnav = false
        }
      }
      if (this.hideSubnav === true) {
        this.setExpanded(false)
      }
    },
  },
  mounted() {
    this.expandIfSelected()
  },
  methods: {
    ...mapMutations(["CLOSE_MOBILEMENU"]),
    select() {
      this.CLOSE_MOBILEMENU()
    },
    setExpanded(bool) {
      this.expanded = bool
    },
    handleToggle() {
      this.setExpanded(!this.expanded)
      this.clickedOpen = true
    },
    expandIfSelected() {
      let containsCurrentPath = false
      for (let option of this.option.subnav) {
        if (`/${this.subsection}/${option.route}` === this.$route.path) {
          containsCurrentPath = true
          break
        }
      }
      if (containsCurrentPath) {
        this.expanded = true
      }
    },
    urlLink(path) {
      return this.$route.path.includes(this.subsection)
        ? `/${this.subsection}/${path}`
        : path
    },
    navItemClass(route) {
      let classes = "block text-left py-4 px-14 text-md text-white select-none font-semibold lg:pb-2 lg:px-6 lg:py-2 lg:hover:bg-gray-lightest/80 lg:active:bg-gray-light border-transparent lg:hover:border-primary lg:active:outline-none lg:border-l-4 lg:focus:bg-gray-lightest/80 lg:focus:border-primary "
      if (this.path.includes(route)) {
        classes += "bg-secondary-600 dark:bg-white/[.06] text-opacity-100 dark:lg:text-white/90 dark:lg:bg-white/[.04] dark:lg:hover:bg-white/[.06] focus:bg-secondary-600 dark:lg:focus:bg-white/[.06] dark:lg:border-primary-300/70 lg:focus:bg-gray-light lg:text-black lg:font-medium lg:bg-gray-light lg:bg-opacity-80 lg:border-primary "
      }
      else {
        classes += "focus:bg-secondary-600 dark:focus:bg-white/[.06] lg:focus:bg-transparent lg:text-gray-400 lg:bg-opacity-100 lg:bg-white dark:lg:bg-navy-900 lg:hover:bg-gray-lightest/80 dark:lg:hover:bg-white/[.04] lg:hover:border-primary lg:active:outline-none lg:focus:bg-gray-lightest/80 dark:lg:focus:bg-white/[.04] lg:focus:border-primary dark:lg:hover:border-primary-300/70 dark:lg:focus:border-primary-300/70"
      }
      return classes
    },
    checkOptionsSidemenuPermissions(option) {
      if (option.feature && option.view == null) {
        // if option has a feature, check if feature is available
        if (option.c5 && !this.isC5) return false
        if (this.getIsTenantSuperuser) return true
        if (option.internal) return false
        return this.availableFeatures?.[option.feature]
      }
      if (!option.view) return true
      // check Site Command version features
      if (this.subsection === "SiteCommand") {
        if (option.c5 && !this.isC5) return false
        if (option.view === "edl" || option.view === "cil") {
          if (!this.getSiteCommandControllerFeatures?.includes("EDL_CIL")) return false
        }
        if (option.route.split("/")[0] === "assistance_tool") {
          if (option.view === "mirror_bumps") {
            if (!this.getSiteCommandControllerFeatures?.includes("Bumps_Mirror")
              && !this.getSiteCommandControllerFeatures?.includes("Bumps_Wrap_HB")
            ) return false
          }
          if (option.view === "control_word") {
            if (!this.getSiteCommandControllerFeatures?.includes("Control_Word_C5")
              && !this.getSiteCommandControllerFeatures?.includes("Control_Word_C4")
              && !this.getSiteCommandControllerFeatures?.includes("Control_Word_Standard_Offering")
              && !this.getSiteCommandControllerFeatures?.includes("Requeue_Control_Word")
            ) return false
          }
          if (option.view === "omni_control") {
            if (!this.getSiteCommandControllerFeatures?.includes("Omni_Control")) return false
          }
          if (option.view === "omni_vac") {
            if (!this.getSiteCommandControllerFeatures?.includes("Omni_Vac")) return false
          }
          if (option.view === "airgate_tables") {
            if (!this.getSiteCommandControllerFeatures?.includes("Airgate_Table")) return false
          }
          if (option.view === "dry_run") {
            if (!this.getSiteCommandControllerFeatures?.includes("Dry_Run")) return false
          }
          if (option.view === "conveyor_speed_selector") {
            if (!this.getSiteCommandControllerFeatures?.includes("Conveyor_Speed_Config")) return false
          }
          if (option.view === "pid_attack_module") {
            if (!this.getSiteCommandControllerFeatures?.includes("PID_Attack_Module")) return false
          }
          if (option.view === "blower_flips") {
            if (!this.getSiteCommandControllerFeatures?.includes("Blower_Flips")) return false
          }
        }
      }
      if (this.getIsTenantSuperuser) return true
      if (option.internal) return false
      // show Users & Permissions + chem thresholds if user has user_management privileges
      if (this.getUserManagementAccess && (
        option.route === "Users" || option.route === "Permissions" || option.route === "Thresholds/chemical"
      )) return true
      if (option.washlink  && !this.getPOSSystem) return false
      return this.availableViews?.[option.view]
    }
  },
}
</script>

<style scoped>
.subSideMenuButton {
  min-height: 64px !important;
  line-height: 1.4;
}
</style>